import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { DisclaimerNote, SiteFooter } from "@/components/site-footer";
import { SiteHeader } from "@/components/site-header";
import { StatusPill } from "@/components/status-pill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { passwordSchema } from "@/routes/auth";
import { supabase } from "@/integrations/supabase/client";
import { relativeTime } from "@/lib/format";
import {
  deleteAllData,
  ensureProfile,
  getProfile,
  listJobs,
  updateProfile,
} from "@/lib/jobs.functions";

export const Route = createFileRoute("/_authenticated/account")({
  head: () => ({
    meta: [
      { title: "Account & your data — Understand Your Genome" },
      {
        name: "description",
        content:
          "Manage your email notifications, review every file you've uploaded, and permanently delete your data whenever you want.",
      },
      { property: "og:title", content: "Account & your data" },
      {
        property: "og:description",
        content: "Notification settings, your uploads, and one-click data deletion.",
      },
    ],
  }),
  component: AccountPage,
});

function AccountPage() {
  const [email, setEmail] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const fetchProfile = useServerFn(getProfile);
  const makeProfile = useServerFn(ensureProfile);
  const saveProfile = useServerFn(updateProfile);
  const fetchJobs = useServerFn(listJobs);
  const wipe = useServerFn(deleteAllData);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setEmail(data.user?.email ?? null));
    makeProfile({}).catch(() => undefined);
  }, [makeProfile]);

  const { data: profile } = useQuery({ queryKey: ["profile"], queryFn: () => fetchProfile({}) });
  const { data: jobs } = useQuery({ queryKey: ["jobs"], queryFn: () => fetchJobs({}) });

  const notifyMutation = useMutation({
    mutationFn: (notify: boolean) => saveProfile({ data: { notify_on_complete: notify } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["profile"] }),
    onError: () => toast.error("Couldn't save that. Please try again."),
  });

  async function handlePasswordChange(event: React.FormEvent) {
    event.preventDefault();
    const parsed = passwordSchema.safeParse(newPassword);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Please check your password");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("The two passwords don't match.");
      return;
    }
    setSavingPassword(true);
    const { error } = await supabase.auth.updateUser({ password: parsed.data });
    setSavingPassword(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setNewPassword("");
    setConfirmPassword("");
    toast.success("Your password has been updated.");
  }

  async function handleWipe() {
    if (
      !window.confirm(
        "Permanently delete every file you've uploaded and all results we produced? This cannot be undone.",
      )
    )
      return;
    try {
      await wipe({});
      await queryClient.invalidateQueries();
      toast.success("Everything has been deleted.");
    } catch {
      toast.error("We couldn't complete the deletion. Please try again.");
    }
  }

  async function handleSignOut() {
    await supabase.auth.signOut();
    queryClient.clear();
    navigate({ to: "/" });
  }

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="mx-auto w-full max-w-3xl flex-1 px-5 py-14">
        <h1 className="text-3xl">Account &amp; your data</h1>

        <section className="mt-8 rounded-3xl border border-border bg-card p-6">
          <h2 className="text-xl">Your email</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            You're signed in as <span className="font-medium text-foreground">{email ?? "…"}</span>.
            This address is tied to your account, and it's also how we reach you when an annotated
            file is ready. To use a different address, sign out and sign in with it.
          </p>
          <Button variant="outline" className="mt-4" onClick={handleSignOut}>
            Sign out
          </Button>
        </section>

        <section className="mt-6 rounded-3xl border border-border bg-card p-6">
          <h2 className="text-xl">Notifications</h2>
          <label className="mt-4 flex items-center gap-3 text-sm">
            <Switch
              checked={profile?.notify_on_complete ?? true}
              onCheckedChange={(checked) => notifyMutation.mutate(checked)}
            />
            Email me when an analysis finishes
          </label>
        </section>

        <section className="mt-6 rounded-3xl border border-border bg-card p-6">
          <h2 className="text-xl">Password</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Set or change the password for your account — useful if you signed up with Google or an
            email link and haven't chosen one yet.
          </p>
          <form onSubmit={handlePasswordChange} className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="account-new-password">New password</Label>
              <Input
                id="account-new-password"
                type="password"
                autoComplete="new-password"
                placeholder="At least 6 characters"
                maxLength={128}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="account-confirm-password">Confirm new password</Label>
              <Input
                id="account-confirm-password"
                type="password"
                autoComplete="new-password"
                placeholder="Repeat it"
                maxLength={128}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            <Button type="submit" variant="outline" disabled={savingPassword}>
              {savingPassword ? "One moment…" : "Set new password"}
            </Button>
          </form>
        </section>

        <section className="mt-6 rounded-3xl border border-border bg-card p-6">
          <h2 className="text-xl">Your uploads</h2>
          {!jobs || jobs.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">
              You haven't uploaded anything yet.{" "}
              <Link to="/upload" className="text-primary underline underline-offset-4">
                Upload a file
              </Link>
              .
            </p>
          ) : (
            <ul className="mt-4 divide-y divide-border">
              {jobs.map((job) => (
                <li key={job.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                  <div>
                    <p className="break-all text-sm font-medium">{job.label || job.filename}</p>
                    <p className="text-xs text-muted-foreground">
                      Uploaded {relativeTime(job.created_at)}
                    </p>
                  </div>
                  <StatusPill status={job.status} />
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="mt-6 rounded-3xl border border-destructive/30 bg-card p-6">
          <h2 className="text-xl">Delete my data</h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            We keep your uploaded files and results only for as long as you want them. Deleting
            removes the actual files from storage and erases every finding we generated — it isn't
            a "deactivate" switch, and there's no hidden copy kept for analytics or training.
          </p>
          <Button variant="destructive" className="mt-4" onClick={handleWipe}>
            Delete all my files and results
          </Button>
        </section>

        <DisclaimerNote className="mt-10" />
      </main>
      <SiteFooter />
    </div>
  );
}
